import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {useEffect} from 'react';
import {toast} from 'react-toastify';
import {IPresentationParamValues, Param} from '../../../../incase-domain/presentationParamValues/IPresentationParamValues';
import {
    addPresentationParamValueService,
    deletePresentationParamValueService,
    fetchPresentationParamValuesService,
} from '../../../../service/presentationParamValues.service';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {IApiError} from '../../../../incase-domain/error';

interface IFetchPresentationParamValuesProps {
    presentationId: string;
    enabled: boolean;
    onSuccess?: (data: IPresentationParamValues) => void;
}

export const useFetchPresParamValues = ({
    presentationId,
    enabled,
    onSuccess,
}: IFetchPresentationParamValuesProps): UseQueryResult<IPresentationParamValues> => {
    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
    }, [presentationId, queryClient]);

    return useQuery({
        queryKey: QUERY_KEYS.presentationParam.byPresentation(presentationId),
        queryFn: () => fetchPresentationParamValuesService(presentationId),
        onSuccess: (params) => {
            if (onSuccess && params) {
                onSuccess(params);
            }
        },
        enabled,
    });
};

interface IAddPresentationParamValuesProps {
    presentationId: string;
    newParam: Param[];
}

export const useAddOrUpdateParam = (): UseMutationResult<IPresentationParamValues, IApiError, IAddPresentationParamValuesProps> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({presentationId, newParam}: IAddPresentationParamValuesProps) =>
            addPresentationParamValueService(presentationId, newParam),
        onSuccess: (res, {presentationId}) => {
            if (res && res.errors && res.errors.message) {
                toast.error(res.errors.message);
            }
            queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.byPresentation(presentationId));
        },
    });
};

interface IDeletePresentationParamValuesProps {
    presentationId: string;
    paramsToDelete: Param[];
}

export const useDeleteParam = (): UseMutationResult<IPresentationParamValues, unknown, IDeletePresentationParamValuesProps> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({presentationId, paramsToDelete}: IDeletePresentationParamValuesProps) =>
            deletePresentationParamValueService(presentationId, paramsToDelete),
        onSuccess: (_, {presentationId}) => {
            queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.byPresentation(presentationId));
        },
    });
};
